<template>
	<div class="reservation">
		<ValidationObserver
			v-if="isEdit || isNew"
			class="reservation__form"
			ref="reservationForm"
			v-slot="{ invalid, errors }"
			tag="form"
			@submit.prevent="onSubmitReservation"
			novalidate
		>
			<section
				v-if="isNew"
				class="reservation__block"
			>
				<div
					v-if="!isLoggedIn"
					class="reservation__login"
				>
					<div
						class="reservation__login-text"
						v-html="$t('reservation.loginText')"
					/>
					<Button
						@click.prevent="onLoginClick"
						level="tertiary"
						type="button"
						:text="$t('reservation.loginButton')"
						arrow
					/>
				</div>
			</section>
			<h1
				class="reservation__title"
				v-html="mainTitle"
			/>

			<GeneralMessage
				ref="reservationResponse"
				v-if="reservationResponse.message"
				:message="reservationResponse.message"
				:type="reservationResponse.type"
				@close="reservationResponse.message = ''"
			/>

			<ValidationProvider
				v-if="restaurantOptions.length"
				class="reservation__block"
				name="restaurant"
				rules="required"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="section"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<h2
					class="reservation__subtitle"
					v-html="$t('reservation.restaurantTitle')"
				/>
				<FormInputSelect
					v-model="selectedRestaurant"
					:options="restaurantOptions"
					:label="$t('reservation.restaurantLabel')"
					:disabled="isEdit"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>

				<OrderBoxLocation
					v-if="selectedRestaurant"
					:location="reservationRestaurant"
					shipping-method="takeaway"
					no-click
				/>
			</ValidationProvider>

			<section
				v-else-if="initialized"
				class="reservation__block"
			>
				<h2
					class="reservation__error"
					v-html="$t('reservation.errors.noRestaurants')"
				/>
			</section>

			<section
				v-if="selectedRestaurant"
				class="reservation__block"
			>
				<h2
					class="reservation__subtitle"
					v-html="$t('reservation.seatsTitle')"
				/>
				<QuantityCounter
					:quantity="selectedSeats"
					base-class="reservation__seats"
					:locked="formLocked"
					:min="minSeats"
					:max="maxSeats"
					@sub="onDecreaseSeat()"
					@add="onIncreaseSeat()"
				/>
			</section>

			<ValidationProvider
				v-if="selectedRestaurant"
				class="reservation__block"
				name="date"
				rules="required"
				v-slot="{ errors, invalid, validated }"
				tag="section"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<h2
					class="reservation__subtitle"
					v-html="$t('reservation.dateTitle')"
				/>
				<AvailabilityCalendar
					v-model="selectedDate"
					:loading="monthLoading"
					:availabilities="reservationMonthAvailabilities"
					@change:month="onMonthChange"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<section
				class="reservation__block"
				v-if="selectedDate && reservationRules && !showReservationEvents && !reservationOnlyByPhone"
			>
				<div
					class="reservation__text"
					v-html="$options.filters.fwgcmstext(reservationRules)"
				/>
			</section>
			<template v-else-if="selectedDate && showReservationEvents && !reservationOnlyByPhone">
				<section
					v-for="event in reservationEvents"
					class="reservation__block reservation__block--image"
				>
					<div
						v-if="event.image"
						class="reservation__image"
					>
						<img
							class="reservation__image-element"
							:src="event.image.src"
							:srcset="event.image.srcset"
						/>
					</div>
					<div
						class="reservation__text"
						v-html="event.text"
					/>
				</section>
			</template>

			<ValidationProvider
				v-if="reservationRooms.length > 1 && !reservationOnlyByPhone"
				class="reservation__block"
				name="room"
				rules="required"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="section"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<h2
					class="reservation__subtitle"
					v-html="$t('reservation.roomTitle')"
				/>
				<FormInputSelect
					v-model="selectedRoom"
					:options="roomOptions"
					:label="$t('reservation.roomLabel')"
					required
				/>
				<span
					v-if="selectedRoom && !indiningAvailable"
					class="additional-info"
					v-html="$t('reservation.indining.notAvailable')"
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<template
				v-if="showTimeSelect"
			>
				<ValidationProvider
					class="reservation__block"
					name="time"
					rules="required"
					v-slot="{ errors, invalid, validated }"
					tag="section"
					:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
				>
					<h2
						class="reservation__subtitle"
						v-html="$t('reservation.timeTitle')"
					/>
					<TimePicker
						v-model="selectedTime"
						:times="timeOptions"
						:label="$t('forms.labels.time')"
					/>
					<span
						class="input-error"
						v-if="invalid && validated"
						v-html="errors[0]"
					/>
          <GeneralMessage
            v-if="showAsyncMessage"
            type="warning"
            :message="`* ${$t('reservation.asyncMessage')}`"
            stick
          />
				</ValidationProvider>

				<section
					v-if="reservationVegiSeats"
					class="reservation__block"
				>
					<h2
						class="reservation__subtitle"
						v-html="$t('reservation.vegiSeats.title')"
					/>
					<div
						class="reservation__text"
						v-html="$t('reservation.vegiSeats.text')"
					/>
					<ValidationProvider
						name="addVegiSeats"
						rules="required"
						tag="div"
						v-slot="{ errors, invalid, validated, dirty }"
						:customMessages="{ required: $t('reservation.vegiSeats.error') }"
					>
						<FormInputRadio
							name="addVegiSeats"
							:options="[
								{
									value: '0',
									label: $t('reservation.vegiSeats.no')
								},
								{
									value: '1',
									label: $t('reservation.vegiSeats.yes')
								},
							]"
							v-model="addVegiSeats"
							:disabled="formLocked"
							vertical
							inline
							required
						/>
						<span
							class="input-error"
							v-if="invalid && validated"
							v-html="errors[0]"
						/>
					</ValidationProvider>

					<template v-if="form.vegiSeats">
						<ValidationProvider
							name="vegiSeats"
							rules="min_value:1"
							tag="div"
							v-slot="{ errors, invalid, validated, dirty }"
							:customMessages="{ min_value: $t('reservation.vegiSeats.errorQuantity') }"
						>
							<div
								class="reservation__text"
								v-html="$t('reservation.vegiSeats.quantityLabel')"
							/>
							<input type="hidden" :value="form.vegiSeats"/>
							<QuantityCounter
								:quantity="form.vegiSeats"
								base-class="reservation__seats"
								:locked="formLocked"
								:min="1"
								:max="selectedSeats"
								@sub="onDecreaseVegiSeat()"
								@add="onIncreaseVegiSeat()"
							/>
							<div
								class="input-error input-error--margin"
								v-if="invalid && validated"
								v-html="errors[0]"
							/>
						</ValidationProvider>
					</template>
				</section>

				<section
					v-if="reservationChoicesWithoutSpecials.length"
					class="reservation__block"
				>
					<!-- <h2
						class="reservation__subtitle"
						v-html="$t('reservation.choicesTitle')"
					/> -->
					<ValidationProvider
						v-for="(choice, index) in reservationChoicesWithoutSpecials"
						:key="`choice-${index}`"
						:rules="choice.info.required ? { required: { allowFalse: false } } : ''"
						:name="`choice-${index}`"
						tag="div"
						v-slot="{ invalid, errors, validated }"
						:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
					>
						<div
							v-if="choice.info.label"
							class="reservation__lead"
							v-html="choice.info.label"
						/>
						<div
							v-if="choice.info.infotext"
							class="reservation__text"
							v-html="choice.info.infotext"
						/>
						<FormInputRadio
							v-if="choice.info.options.length > 1"
							:name="`choice-${index}`"
							:options="choice.info.options.map(c => ({ label: c.label, value: c.label }))"
							:value="form.choices[index]"
							:disabled="formLocked"
							:required="choice.info.required"
							vertical
							@input="onChoicesChange($event, index)"
						/>
						<FormInputCheckbox
							v-else
							:name="`choice-${index}`"
							:checked="!!form.choices[index]"
							:disabled="formLocked"
							:required="choice.info.required"
							:label="choice.info.options[0].label"
							@input="onChoicesChange($event ? choice.info.options[0].label : null, index, choice)"
						/>
						<span
							class="input-error"
							v-if="invalid && validated"
							v-html="errors[0]"
						/>
					</ValidationProvider>
				</section>

				<section
					v-if="indiningAvailable"
					class="reservation__block"
				>
					<h2
						class="reservation__subtitle"
						v-html="$t('reservation.indining.optionTitle')"
					/>
					<div
						class="reservation__text"
						v-html="$t('reservation.indining.optionText')"
					/>
					<FormInputCheckbox
						name="indining"
						v-model="form.indining"
						:disabled="formLocked"
					>
						<span v-html="$t('reservation.indining.optionLabel')"/>
					</FormInputCheckbox>
				</section>

				<section
					v-if="formReady"
					class="reservation__block"
				>
					<h2
						class="reservation__subtitle"
						v-html="detailsTitle"
					/>

					<ValidationProvider
						v-if="isNew"
						name="firstname"
						rules="required"
						v-slot="{ errors, invalid, validated }"
						tag="div"
						:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
					>
						<FormInput
							type="text"
							:placeholder="$t('forms.labels.firstname')"
							:label="$t('forms.labels.firstname')"
							name="firstname"
							v-model="form.firstName"
							:error="errors.length > 0"
							required
						/>
						<span
							class="input-error"
							v-if="invalid && validated"
							v-html="errors[0]"
						/>
					</ValidationProvider>
					<ValidationProvider
						v-if="isNew"
						name="lastname"
						rules="required"
						v-slot="{ errors, invalid, validated }"
						tag="div"
						:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
					>
						<FormInput
							type="text"
							:placeholder="$t('forms.labels.lastname')"
							:label="$t('forms.labels.lastname')"
							name="lastname"
							v-model="form.lastName"
							:error="errors.length > 0"
							required
						/>
						<span
							class="input-error"
							v-if="invalid && validated"
							v-html="errors[0]"
						/>
					</ValidationProvider>

					<ValidationProvider
						v-if="isNew"
						name="phone"
						rules="required|phone"
						v-slot="{ errors, invalid, validated }"
						tag="div"
						:customMessages="{ required: $t('forms.errors.fieldEmpty'), phone: $t('forms.errors.phone') }"
					>
						<FormInput
							type="tel"
							:placeholder="$t('forms.labels.phone')"
							:label="$t('forms.labels.phone')"
							name="phone"
							v-model="form.phoneNumber"
							:error="errors.length > 0"
							required
						/>
						<span
							class="input-error"
							v-if="invalid && validated"
							v-html="errors[0]"
						/>
						<span class="additional-info" v-html="$t('reservation.phoneHint')"/>
					</ValidationProvider>

					<ValidationProvider
						v-if="isNew"
						name="email"
						rules="required|email"
						v-slot="{ errors, invalid, validated }"
						tag="div"
						:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
					>
						<FormInput
							type="email"
							name="email"
							autocomplete="email"
							:placeholder="$t('forms.labels.email')"
							:label="$t('forms.labels.email')"
							v-model="form.email"
							:error="errors.length > 0"
							:readonly="!!isLoggedIn"
							required
						/>
						<span
							class="input-error"
							v-if="invalid && validated"
							v-html="errors[0]"
						/>
						<span class="additional-info" v-html="$t('reservation.emailHint')"/>
					</ValidationProvider>

					<FormInputTextarea
						:placeholder="$t('forms.labels.notes')"
						:label="$t('forms.labels.notes')"
						v-model="form.notes"
					/>

					<ValidationProvider
						:rules="{ required: { allowFalse: false } }"
						name="acceptTerms"
						tag="div"
						v-slot="{ invalid, errors, validated }"
						:customMessages="{ required: $t('reservation.errorTerms') }"
					>
						<FormInputCheckbox
							name="terms"
							value="acceptTerms"
							v-model="form.acceptTerms"
							required
							immediate
							block
						>
							<span v-html="$t('reservation.termsLabel')"/>
						</FormInputCheckbox>
						<!-- TODO: this url should be dynamic -->
						<span
							class="input-error"
							v-if="invalid && validated"
							v-html="errors[0]"
						/>
					</ValidationProvider>

					<ValidationProvider
						:rules="{ required: { allowFalse: false } }"
						name="acceptPrivacy"
						tag="div"
						v-slot="{ invalid, errors, validated }"
						:customMessages="{ required: $t('reservation.errorPrivacy') }"
					>
						<FormInputCheckbox
							name="terms"
							value="acceptPrivacy"
							v-model="form.acceptPrivacy"
							required
							immediate
							block
						>
							<span v-html="$t('reservation.privacyLabel')"/>
						</FormInputCheckbox>
						<!-- TODO: this url should be dynamic -->
						<span
							class="input-error"
							v-if="invalid && validated"
							v-html="errors[0]"
						/>
					</ValidationProvider>

					<Button
						type="submit"
						level="primary"
						:text="$t('reservation.submitButton')"
						:disabled="formLocked || !formReady"
					/>
				</section>
				<section
					v-if="isEdit"
					class="reservation__block"
				>
					<Button
						type="button"
						level="tertiary"
						:text="$t('reservation.cancelReservation')"
						:disabled="formLocked || !formReady"
						@click="onReservationCancel"
						arrow
					/>
				</section>
			</template>

			<section
				v-else-if="reservationOnlyByPhone"
				class="reservation__block"
			>
				<h2
					class="reservation__error"
					v-html="$t('reservation.errors.pastCutoff', { phone: reservationRestaurant.phoneNumber, phoneRaw: reservationRestaurant.phoneNumber.replace(/(\(\d\))|[\s-_]/g, '')})"
				/>
			</section>

			<section
				v-else-if="(reservationAvailabilitiesLoaded && !reservationLoading) && reservationDate && (!timeOptions.length || timesDisabled) && (!reservationRooms.length || (reservationRooms.length && reservationRoom))"
				class="reservation__block"
			>
				<h2
					v-if="timesMaxSeats && timesMaxSeats < selectedSeats"
					class="reservation__error"
					v-html="$t('reservation.errors.notEnoughSeats', { max: timesMaxSeats, phone: reservationRestaurant.phoneNumber, phoneRaw: reservationRestaurant.phoneNumber.replace(/(\(\d\))|[\s-_]/g, '')})"
				/>
				<h2
					v-else
					class="reservation__error"
					v-html="$t('reservation.errors.noDates')"
				/>
			</section>
		</ValidationObserver>

		<div
			v-else-if="isComplete || isCanceled || isExpired"
		>
			<h1
				class="reservation__title"
				v-html="completedTitle"
			/>
			<div
				class="reservation__text"
				v-html="completedText"
			/>

      <GeneralMessage
        v-if="reservationDetails && reservationDetails.pending"
        type="warning"
        :message="$t('reservation.complete.pending')"
        stick
      />

			<section class="reservation__block">
				<h2
					class="reservation__subtitle"
					v-html="$t('reservation.complete.reservationTitle')"
				/>

				<ul class="reservation__details">
					<li class="reservation__detail">
						<strong
							class="reservation__detail-label"
							v-html="$t('reservation.complete.detailsName')"
						/>
						<span
							class="reservation__detail-value"
							v-html="`${form.firstName} ${form.lastName}`"
						/>
					</li>
					<li class="reservation__detail">
						<strong
							class="reservation__detail-label"
							v-html="$t('reservation.complete.detailsRestaurant')"
						/>
						<span
							class="reservation__detail-value"
							v-html="reservationRestaurantName"
						/>
					</li>
					<li class="reservation__detail">
						<strong
							class="reservation__detail-label"
							v-html="$t('reservation.complete.detailsPeople')"
						/>
						<span
							class="reservation__detail-value"
							v-html="reservationSeats"
						/>
					</li>
					<li
						v-if="reservationDetails && reservationDetails.roomName"
						class="reservation__detail"
					>
						<strong
							class="reservation__detail-label"
							v-html="$t('reservation.complete.detailsRoom')"
						/>
						<span
							class="reservation__detail-value"
							v-html="reservationDetails.roomName"
						/>
					</li>
					<li
						class="reservation__detail"
						:class="{
							'reservation__detail--canceled': isCanceled
						}"
					>
						<strong
							class="reservation__detail-label"
							v-html="$t('reservation.complete.detailsDate')"
						/>
						<span
							class="reservation__detail-value"
							v-html="reservationDate"
						/>
					</li>
					<li
						class="reservation__detail"
						:class="{
							'reservation__detail--canceled': isCanceled
						}"
					>
						<strong
							class="reservation__detail-label"
							v-html="$t('reservation.complete.detailsTime')"
						/>
						<span
							class="reservation__detail-value"
							v-html="reservationTimeEnd ? `${reservationTime} - ${reservationTimeEnd}` : reservationTime"
						/>
					</li>
					<li
						v-if="!isCanceled && !isExpired && reservationDetails && reservationDetails.editable"
						class="reservation__detail reservation__detail--buttons"
					>
						<Button
							class="account__reservation-button"
							type="button"
							:title="$t('account.reservations.cancelButton')"
							level="secondary"
							@click="onReservationCancel()"
							icon="trash"
							small
						/>
						<Button
							v-if="!isIndining"
							class="account__reservation-button"
							type="button"
							:title="$t('account.reservations.editButton')"
							level="primary"
							@click="onReservationEdit()"
							icon="edit"
							small
						/>
					</li>
				</ul>

        <input
          v-if="reservationDetails.emailHash256"
          type="hidden"
          name="email-hash"
          :value="reservationDetails.emailHash256"
        />

				<Button
					v-if="!isCanceled && !isExpired && reservationCalendar"
					type="button"
					level="tertiary"
					:text="$t('reservation.calendarButton')"
					:disabled="loading"
					:href="reservationCalendarUrl"
					icon="date"
					arrow
					:download="$t('reservation.calendarFile', { date: reservationDate })"
				/>
			</section>

			<template v-if="!isCanceled && !isExpired && (indiningAvailable || (reservationDetails && reservationDetails.indiningAvailable))">
				<GeneralMessage
					v-if="indiningResponse.message"
					:message="indiningResponse.message"
					:type="indiningResponse.type"
					@close="indiningResponse.message = ''"
				/>

				<div
					v-if="teaserImage && teaserImage.src"
					class="reservation__teaser"
					@click="onIndiningOrder"
				>
					<figure class="reservation__teaser-image">
						<img
							class="reservation__teaser-image-element"
							:src="teaserImage.src"
							:srcset="teaserImage.srcset"
							alt="teaser-background"
						/>
					</figure>
					<div class="reservation__teaser-content">
						<h3
							class="reservation__teaser-title"
							v-html="$t('reservation.complete.indiningTitle')"
						/>
						<div
							class="reservation__teaser-text"
							v-html="$t('reservation.complete.indiningText')"
						/>
						<Button
							class="reservation__teaser-button"
							level="tertiary"
							:text="$t('reservation.complete.indiningButton')"
							arrow
							:disabled="loading"
						/>
					</div>
				</div>
				<section
					v-else
					class="reservation__block"
					@click="onIndiningOrder"
				>
					<h3
						class="reservation__subtitle"
						v-html="$t('reservation.complete.indiningTitle')"
					/>
					<div
						class="reservation__text"
						v-html="$t('reservation.complete.indiningText')"
					/>
					<Button
						level="tertiary"
						:text="$t('reservation.complete.indiningButton')"
						arrow
						:disabled="loading"
					/>
				</section>
			</template>

			<section
				v-if="!isIndining || isCanceled || isExpired"
				class="reservation__block"
			>
				<Button
					type="button"
					level="secondary"
					:text="$t('reservation.newReservation')"
					:disabled="loading"
					@click="onNewReservation"
				/>
			</section>
		</div>
	</div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate"
import { mapGetters } from "vuex"
import scrrroll from 'scrrroll'
import GenericMixin from '@/mixins/GenericVueMixin'
import FormInputSelect from '@/components/FormInputSelectCustom';
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputRadio from "@/components/FormInputRadio"
import FormInput from "@/components/FormInput"
import FormInputTextarea from "@/components/FormInputTextarea"
import TimePicker from "@/components/TimePicker"
import Button from '@/components/Button';
import GeneralMessage from '@/components/GeneralMessage';
import QuantityCounter from '@/components/QuantityCounter';
import OrderBoxLocation from '@/components/OrderBoxLocation';
import AvailabilityCalendar from '@/components/AvailabilityCalendar';
import { compareArrays } from '@/utils/comparearrays';
import { getUrlParams, urlParams, makeUrl } from '@/utils/urlParams';
import htmlComponent from "htmlcomponent"
import leadingZero from '@/utils/leadingZero'

export default {
	mixins: [
		GenericMixin
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInputCheckbox,
		FormInputRadio,
		FormInputSelect,
		QuantityCounter,
		FormInput,
		FormInputTextarea,
		TimePicker,
		Button,
		GeneralMessage,
		OrderBoxLocation,
		AvailabilityCalendar,
	},
	data() {
		return {
			initialized: false,
			seatTimeout: null,
			minSeats: 1,
			maxSeats: Infinity,
			addVegiSeats: null,
			form: {
				email: '',
				firstName: '',
				lastName: '',
				phoneNumber: '',
				notes: '',
				choices: [],
				indining: false,
				vegiSeats: 0,
				acceptTerms: false,
				acceptPrivacy: false,
			},
			reservationResponse: {
				type: 'info',
				message: '',
			},
			indiningResponse: {
				type: 'info',
				message: '',
			},
			mode: '',
			teaserImage: this.$root.options.teaserImage,
			monthLoading: false,
			currentMonth: (new Date()).getMonth() + 1,
			currentYear: (new Date()).getFullYear(),
		};
	},
	computed: {
		...mapGetters('reservation', [
			'reservationRestaurants',
			'reservationRestaurant',
			'reservationRestaurantName',
			'reservationSeats',
			'reservationRooms',
			'reservationRoom',
			'reservationAvailabilities',
			'reservationAvailabilitiesLoaded',
			'reservationDate',
			'reservationTimes',
			'reservationTime',
			'reservationTimeEnd',
			'reservationAvailability',
			'reservationChoices',
			'reservationChoicesWithoutSpecials',
			'reservationVegiSeats',
			'reservationNoIndining',
			'reservationRules',
			'reservationEvents',
			'showReservationEvents',
			'reservationHash',
			'reservationDetails',
			'reservationCalendar',
			'reservationCalendarUrl',
			'reservationLoading',
			'reservationMonthAvailabilities',
			'reservationOnlyByPhone',
		]),
		...mapGetters("account", [
			"isLoggedIn",
			"accountUserDetail",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		mainTitle() {
			return this.edit ? this.$t('reservation.editTitle') : this.$t('reservation.indining.title')
		},
		detailsTitle() {
			return this.edit ? this.$t('reservation.detailsEditTitle') : this.$t('reservation.detailsTitle')
		},
		completedTitle() {
			return this.$t(`reservation.${this.mode}.title`)
		},
		completedText() {
			return this.$t(`reservation.${this.mode}.text`)
		},
		selectedRestaurant: {
			async set(channel) {
				this.$store.dispatch('reservation/setRestaurant', { channel });
				this.loadAvailabilities();
			},
			get() {
				return this.reservationRestaurant?.code || '';
			}
		},
		selectedSeats: {
			set(seats) {
				this.$store.dispatch('reservation/setSeats', { seats });
				clearTimeout(this.seatTimeout);
				this.seatTimeout = setTimeout(async () => {
					this.loadAvailabilities();
				}, 300);
			},
			get() {
				return this.reservationSeats;
			}
		},
		selectedRoom: {
			set(room) {
				this.$store.dispatch('reservation/setRoom', { room });
			},
			get() {
				return this.reservationRoom?.id;
			}
		},
		selectedDate: {
			set(date) {
				this.$store.dispatch('reservation/setDate', { date });
				if (!this.reservationOnlyByPhone) {
					this.loadAvailabilities()
				}
			},
			get() {
				return this.reservationDate;
			}
		},
		selectedTime: {
			set(time) {
				this.$store.dispatch('reservation/setTime', { time });
			},
			get() {
				return this.reservationTime;
			}
		},
		restaurantOptions() {
			if (this.reservationRestaurants) {
				// const restaurants = this.reservationRestaurants.filter(restaurant => this.isIndining ? restaurant.indining : true);
				const restaurants = this.reservationRestaurants;

				if (restaurants.some(r => r.brand !== this.brand)) {
					const restaurantsByBrand = restaurants.reduce((groups, restaurant) => {
						if (!groups[restaurant.brand]) {
							groups[restaurant.brand] = []
						}

						groups[restaurant.brand].push(restaurant);
						return groups;
					}, {});

					return Object.entries(restaurantsByBrand).map(([brand, restaurants]) => {
						return {
							group: true,
							label: this.$t(`brands.${brand}`),
							options: restaurants.map(restaurant => {
								return {
									label: restaurant.name,
									value: restaurant.code,
								}
							})
						}
					});
				}

				return restaurants.map(restaurant => {
					return {
						label: restaurant.name,
						value: restaurant.code,
					}
				});
			}
			return [];
		},
		roomOptions() {
			return this.reservationRooms ?
				this.reservationRooms
					// .filter(room => !this.isIndining || room.indining)
					.map(room => {
						return {
							label: room.name,
							value: room.id,
						}
					}) :
				[];
		},
		timeOptions() {
			return this.reservationTimes.map(t => {
        const isAsync = t.async?.includes(this.selectedSeats)
        return {
          label: t.time + (isAsync ? '*' : ''),
          value: t.time,
          disabled: !t.available || (!!t.async?.length && !t.async.includes(this.selectedSeats)),
        }
      })
		},
		timesDisabled() {
			return !this.reservationLoading && this.reservationTimes.length == this.timeOptions.filter(time => time.disabled).length;
		},
		timesMaxSeats() {
			return this.reservationTimes.reduce((seats, time) => Math.max(time.max, seats), 0);
		},
		formLocked() {
			return this.loading;
		},
		formReady() {
			return !!this.selectedRestaurant && !!this.selectedSeats && this.selectedRoom >= 0 && !!this.selectedDate && !!this.selectedTime;
		},
		isNew() {
			return this.mode === 'new';
		},
		isEdit() {
			return this.mode === 'edit';
		},
		isComplete() {
			return this.mode === 'complete';
		},
		isCanceled() {
			return this.mode === 'canceled';
		},
		isExpired() {
			return this.mode === 'expired';
		},
		isIndining() {
			return !!this.form.indining;
		},
		indiningAvailable() {
			return !!(this.reservationRestaurant?.indining && this.reservationRoom?.indining && !this.reservationNoIndining);
		},
		showTimeSelect() {
			return this.selectedRoom >= 0 && this.timeOptions.length && !this.timesDisabled && !this.reservationOnlyByPhone
		},
    showAsyncMessage() {
      return this.reservationAvailability?.async?.includes(this.selectedSeats)
    }
	},
	watch: {
		reservationRestaurant(newValue, oldValue) {
			if (!newValue?.indining) {
				this.form.indining = false;
			}
			this.onMonthChange()
		},
		reservationRoom(newValue, oldValue) {
			if (!newValue?.indining) {
				this.form.indining = false;
			}
		},
		reservationVegiSeats(newValue) {
			if (!newValue) {
				this.form.vegiSeats = 0;
			}
		},
		reservationChoices(newValue, oldValue) {
			if (this.initialized && !compareArrays(newValue, oldValue)) {
				this.form.choices.splice(0);
			}
		},
		selectedSeats(newValue, oldValue) {
			if (newValue != oldValue) {
				this.form.vegiSeats = Math.min(this.form.vegiSeats, this.selectedSeats);
				this.onMonthChange()
			}
		},
		isLoggedIn(value) {
			if (value) {
				this.onIsLoggedIn()
			}
		},
		addVegiSeats(value) {
			this.form.vegiSeats = +value;
		},
	},
	methods: {
		onLoginClick() {
			htmlComponent.send('overlay:open', {type: 'login'});
		},
		onDecreaseSeat() {
			this.selectedSeats = Math.max(this.minSeats, this.selectedSeats - 1);
		},
		onIncreaseSeat() {
			this.selectedSeats = this.selectedSeats + 1;
		},
		onDecreaseVegiSeat() {
			this.form.vegiSeats = Math.max(0, this.form.vegiSeats - 1);
		},
		onIncreaseVegiSeat() {
			this.form.vegiSeats = Math.min(this.selectedSeats, this.form.vegiSeats + 1);
		},
		onChoicesChange(choice, index) {
			this.form.choices[index] = choice;
		},
    async onError(err, response = 'reservationResponse') {
      let notificationMessage = this.$t('forms.errors.generic');
      if (err.response && err.response.data.message) {
        notificationMessage = err.response.data.errors && Object.values(err.response.data.errors).length
          ? Object.values(err.response.data.errors).flat().join()
          : err.response.data.message
      }

      this[response].message = notificationMessage || this.$t('forms.errors.generic');
      this[response].type = "error";

      await this.$nextTick()
      if (this.$refs[response]) {
        this.$refs[response].scrollTo();
      }
    },
		async loadAvailabilities() {
			this.loading = true;
			try {
				await this.$store.dispatch('reservation/loadAvailabilities');
				this.reservationResponse.message = '';
				this.reservationResponse.type = '';
			}
			catch(err) {
				await this.onError(err)
			}
			this.loading = false;
		},
		async onSubmitReservation() {
			this.loading = true
			const isValid = await this.$refs.reservationForm.validate()
			if (isValid) {
				this.$store.dispatch('notification/clearMessages')

				if (!this.reservationVegiSeats) {
					this.form.vegiSeats = 0;
				}

				if (!this.indiningAvailable) {
					this.form.indining = false
				}

				const action = this.isNew ? 'reservation/createReservation' : 'reservation/editReservation'

				try {
					await this.$store.dispatch(action, {
						...this.form,
						indiningAvailable: this.indiningAvailable,
					});
					this.reservationResponse.message = '';
					this.reservationResponse.type = '';

					history.replaceState({}, '', makeUrl({complete: this.reservationHash}));
					this.mode = 'complete';

					window.scrollTo(0,0);

					this.loading = false;
				} catch(err) {
					await this.onError(err)
					this.loading = false
				}
			} else {
				await this.$nextTick();
				const inputError = this.$refs.reservationForm.$el.querySelector('.input-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
				this.loading = false
			}
		},
		async onReservationEdit() {
			this.loading = true;
			history.replaceState({}, '', makeUrl({edit: this.reservationHash}));
			if (this.reservationDetails.canceled) {
				this.mode = 'canceled';
			}
			else if (!this.reservationDetails.editable) {
				this.mode = 'expired'
			}
			else {
				await Promise.all([
					this.$store.dispatch('reservation/loadRestaurants'),
					this.$store.dispatch('reservation/loadAvailabilities'),
				]);
				this.mode = 'edit';
			}
			this.indiningResponse.message = '';
			this.indiningResponse.type = '';
			this.loading = false;
		},
		async onReservationCancel() {
			const confirmed = await this.$store.dispatch('dialog/showDialog', {
				title: this.$t('account.reservations.cancelDialogTitle'),
				message: this.$t('account.reservations.cancelDialogMessage'),
				confirm: this.$t('account.reservations.cancelDialogConfirm'),
				cancel: this.$t('account.reservations.cancelDialogCancel'),
			});
			if (confirmed) {
				this.loading = true;
				await this.$store.dispatch('reservation/cancelReservation', { reservation: this.reservationHash });
				history.replaceState({}, '', makeUrl({canceled: this.reservationHash}));
				this.mode = 'canceled';

				window.scrollTo(0,0);

				this.indiningResponse.message = '';
				this.indiningResponse.type = '';
				this.loading = false;
			}
		},
		async onNewReservation() {
			this.loading = true;
			await this.$store.dispatch('reservation/resetReservation');
			await this.$store.dispatch('reservation/loadRestaurants');
			history.replaceState({}, '', makeUrl());
			this.mode = 'new';
			this.form.email = '';
			this.form.firstName = '';
			this.form.lastName = '';
			this.form.phoneNumber = '';
			this.form.notes = '';
			this.form.acceptTerms = false;
			this.form.acceptPrivacy = false;
			this.form.choices = [];
			this.form.vegiSeats = 0;
			this.indiningResponse.message = '';
			this.indiningResponse.type = '';

			if (this.isLoggedIn) {
				this.fillForm(this.accountUserDetail)
			}

			window.scrollTo(0,0);

			this.loading = false;
		},
		async onIndiningOrder(e) {
			if (this.loading) {
				return;
			}
			try {
				this.loading = true
				this.indiningResponse.message = '';
				this.indiningResponse.type = '';

				let reservationUser = {
					firstName: this.form.firstName,
					lastName: this.form.lastName,
					email: this.form.email,
					phoneNumber: this.form.phoneNumber,
				}

				if (this.isLoggedIn) {
					reservationUser.email = this.accountUserDetail.email;
				}

				await this.$store.dispatch('openCartAtChannel', {
					channel: this.selectedRestaurant,
					orderNow: false,
					deliverDate: this.reservationDate,
					deliverTime: this.reservationTime,
					zip: 0,
					shippingCode: 'indining',
					replaceOldCart: true,
					reservationHash: this.reservationHash,
					reservationUser,
				})
				// we should get redirected, so non of this code does matter below here
			} catch (err) {
				await this.onError(err, 'indiningResponse')
				this.loading = false
			}
		},
		fillForm(details) {
			Object.keys(this.form).forEach(key => {
				if (details[key] !== undefined) {
					if (Array.isArray(details[key])) {
						this.form[key] = details[key].slice();
					}
					else {
						this.form[key] = details[key];

						if (key === 'vegiSeats') {
							this.addVegiSeats = Math.max(Math.min(details[key], 1), 0).toString();
						}
					}
				}
			});
		},
		onIsLoggedIn() {
			if (!this.reservationDetails) {
				this.fillForm(this.accountUserDetail)
			}
		},
		async onMonthChange({ month = this.currentMonth, year = this.currentYear } = {}) {
			const today = new Date()
			const thisDay = today.getDate();
			const thisMonth = today.getMonth() + 1;
			const thisYear = today.getFullYear();

			let from = [year, leadingZero(month), leadingZero(1)].join('-');
			let to = [year, leadingZero(month), leadingZero(new Date(year, month, 0).getDate())].join('-')

			if (thisMonth === month && thisYear === year) {
				from = [year, leadingZero(month), leadingZero(thisDay)].join('-');
			}

			this.monthLoading = true
			this.currentMonth = month
			this.currentYear = year
      try {
        await this.$store.dispatch('reservation/loadMonthAvailabilities', { from, to })
        this.reservationResponse.message = '';
        this.reservationResponse.type = '';
      } catch (err) {
        await this.onError(err)
      }
			this.monthLoading = false
		}
	},
	async created() {
		this.loading = true;

		const params = getUrlParams();
		// this.form.indining = !!params.indining;

		try {
			if (params.complete) {
				await this.$store.dispatch('reservation/loadReservation', { reservation: params.complete });
				await this.$store.dispatch('reservation/loadReservationCalendar');
				this.fillForm(this.reservationDetails);
				this.mode = 'complete';

				if (params.complete !== this.reservationDetails.reservationHash) {
					history.replaceState({}, '', this.makeUrl({ complete: this.reservationDetails.reservationHash }))
				}
			}
			else if (params.edit) {
				await this.$store.dispatch('reservation/loadReservation', { reservation: params.edit });
				this.fillForm(this.reservationDetails);

				if (this.reservationDetails.canceled) {
					this.mode = 'canceled';
				}
				else if (!this.reservationDetails.editable) {
					this.mode = 'expired'
				}
				else if (this.reservationDetails.indining) {
					await this.$store.dispatch('reservation/loadReservationCalendar')
					this.mode = 'complete'
					history.replaceState({}, '', this.makeUrl({ complete: this.reservationDetails.reservationHash }))
				}
				else {
					await Promise.all([
						this.$store.dispatch('reservation/loadRestaurants'),
						this.$store.dispatch('reservation/loadAvailabilities'),
					])
					this.mode = 'edit';
				}

				if (params.edit !== this.reservationDetails.reservationHash) {
					history.replaceState({}, '', this.makeUrl({ edit: this.reservationDetails.reservationHash }))
				}
			}
			else if (params.canceled) {
				await this.$store.dispatch('reservation/loadReservation', { reservation: params.canceled });
				this.fillForm(this.reservationDetails);
				this.mode = 'canceled';
			}
		}
		catch(err) {
			this.reservationResponse.message = this.$t('reservation.errors.noReservation');
			this.reservationResponse.type = "error";

			await this.$nextTick()
			if (this.$refs.reservationResponse) {
				this.$refs.reservationResponse.scrollTo();
			}
		}

		if (!this.mode) {
			await this.$store.dispatch('reservation/loadRestaurants');
			this.mode = 'new';

			if (params.restaurant && this.reservationRestaurants.find(restaurant => restaurant.code === params.restaurant)) {
				this.selectedRestaurant = params.restaurant;
			}
		}

		this.loading = false;
		this.initialized = true;
	},
	mounted() {
		if (this.isLoggedIn) {
			this.onIsLoggedIn()
		}
	},
}
</script>

<style>

</style>
